import { OTHER_REFERRAL_ID } from './constants';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@medely/ui-kit';
import { useMFAEnrollment } from '@mp/hooks';
import { useFirebaseContext } from '@mp/hooks/useFirebaseContext';
import { AccountInfoFormValues } from './interface';

type SubmitAccountInfoVariables = {
  values: AccountInfoFormValues;
  phoneNumberChanged: boolean;
};

export const useAccountInfoStepMutation = () => {
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { redirectToMFAOnSignup } = useMFAEnrollment();
  const { logout, signInWithToken } = useFirebaseContext();

  const { mutateAsync: submitAccountInfo, isPending: isSubmittingAccountInfo } = useMutation(
    async ({ values, phoneNumberChanged }: SubmitAccountInfoVariables) => {
      // Phone number changes can wipe Firebase MFA enrollment server-side, which
      // can invalidate the current session. Fetch the re-sign-in token BEFORE the
      // update goes out, while the user is still fully authenticated.
      const reSignInToken = phoneNumberChanged
        ? (await axios.post(`/v3/professional/web_view`)).data.token
        : undefined;
      await axios.patch(`/v3/professional/application_wizard/next`, {
        professional: {
          nickname: values.nickname,
          account_attributes: {
            first_name: values.first_name,
            last_name: values.last_name,
            address_zip: values.address_zip,
            phone_number: values.phone_number,
          },
          referral_source_id: values.referral_source_id,
          other_referral_source:
            values.referral_source_id === OTHER_REFERRAL_ID
              ? values.other_referral_source
              : undefined,
        },
      });
      return { reSignInToken };
    },
    {
      onSuccess: async ({ reSignInToken }, { phoneNumberChanged }) => {
        if (phoneNumberChanged) {
          try {
            // Re-sign-in must complete before anything else touches the session.
            await signInWithToken?.(reSignInToken);
          } catch {
            logout?.();
            return;
          }
        }
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        queryClient.invalidateQueries({ queryKey: ['GetCurrentUserForAccountInfoStep'] });
        queryClient.invalidateQueries({ queryKey: ['GetCurrentUserForMarketsStep'] });
        // Unlike the settings/personal-form flow, MFA enrollment is a required step
        // to finish signup. If the user already enrolled earlier in the wizard and
        // then edits their phone number here, enrolledFactors.length is already > 0,
        // so redirectToMFAOnSignup's own checks won't catch a stale enrollment tied
        // to the old number — phoneNumberChanged is what forces re-enrollment.
        redirectToMFAOnSignup(phoneNumberChanged);
      },
      onError: (e: { response?: { data?: { message?: string } } }) => {
        const errorMessage = e?.response?.data?.message;
        toast.error(errorMessage || 'We had an issue, please try again.');
      },
    },
  );

  return { submitAccountInfo, isSubmittingAccountInfo };
};
